import { curry, setKey } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import { chain, debounce } from 'app/_shared/events/util'
import {
  back,
  detail,
  forceRedirect,
  persistChange,
  persistCheck,
  persistOption,
  persistScroll,
  redirect,
  submit,
} from 'app/_shared/events'
import { acl, cell } from 'app/_shared/events/acl'
import createRedirect from 'app/_shared/events/appbar/createRedirect'
import listStatus from 'app/_shared/events/collection/listStatus'
import remember from 'app/_shared/events/remember'
import infinite from 'app/_shared/events/search/infinite'
import infiniteWithDrafts from 'app/_shared/events/search/infiniteWithDrafts'
import persistTab from 'app/_shared/events/tabs/persist'
import platform from 'app/_shared/events/util/platform'
import update from 'app/_shared/events/util/update'
import searchWith from 'app/_shared/events/search/with'
import { _url } from 'app/_shared/events/link'
import actionByKey from 'app/_shared/events/action/byKey'
import dismissKeyboard from 'app/_shared/events/keyboard/dismiss'
import join from 'app/_shared/events/search/query/join'
import fromUserOrgs from 'app/_shared/events/search/query/fromUserOrgs'
import excludeNotConfirmedForRoles
  from 'app/_shared/events/search/query/excludeNotConfirmedForRoles'
import go from 'app/_shared/events/redirect/go'
import {
  withAttributeKey,
  withAttributeKey as redirectWithModelKey,
} from 'app/_shared/events/redirect/withModelKey'
import persistCheckAndAction from 'app/_shared/events/combined/persistCheckAndAction'
import recents from 'app/_shared/events/search/recents'
import searchDevices from 'app/_shared/events/search/device'
import listLocationValues from 'app/_shared/events/collection/listLocationValues'
import searchAndRecreateWithListKey from 'app/_shared/events/searchAndRecreateWithListKey'
import action from 'app/_shared/events/action'
import search from 'app/_shared/events/search'
import copyQRCode from 'app/xEcoApm/event/copyQRCode'
import openQRCode from 'app/xEcoApm/event/openQRCode'
import listTemperatureUnits from 'app/xEcoApm/event/collection/listTemperatureUnits'
import listFrequencyValues from 'app/xEcoApm/event/collection/listFrequencyValues'
import listTankValues from 'app/xEcoApm/event/collection/listTankValues'
import listUsPowerValues from 'app/xEcoApm/event/collection/listUsPowerValues'
import listDateCreated from 'app/xEcoApm/event/collection/listDateCreated'
import openPdf from 'app/xEcoApm/event/openPdf'
import pairDevice from 'app/xEcoApm/event/pairDevice'
import getConnectionStatus from 'app/xEcoApm/event/getConnectionStatus'
import getBatteryStatus from 'app/xEcoApm/event/getBatteryStatus'
import updatePeripheralStatus from 'app/xEcoApm/event/updatePeripheralStatus'
import togglePeripheralConnection from 'app/xEcoApm/event/togglePeripheralConnection'
import resetScan from 'app/xEcoApm/event/resetScan'
import startMeasurement from 'app/xEcoApm/event/measurement/start'
import stopMeasurement from 'app/xEcoApm/event/measurement/stop'
import generateReport from 'app/xEcoApm/event/report/generate'
import stopReport from 'app/xEcoApm/event/report/stop'
import setProject from 'app/xEcoApm/event/setProject'
import displayScanFeedback from 'app/xEcoApm/event/displayScanFeedback'
import toggleItemsLists from 'app/xEcoApm/event/toggleItemsLists'
import formatAcousticSpectrumYLabel from 'app/xEcoApm/event/chart/formatAcousticSpectrumYLabel'
import formatCavitationIntensityYLabel from 'app/xEcoApm/event/chart/formatCavitationIntensityYLabel'
import formatMainFrequencyYLabel from 'app/xEcoApm/event/chart/formatMainFrequencyYLabel'
import formatAcousticSpectrumXLabel from 'app/xEcoApm/event/chart/formatAcousticSpectrumXLabel'
import getBluetoothPeripheralName from 'app/xEcoApm/event/getBluetoothPeripheralName'
import toggleSetAsReference from 'app/xEcoApm/event/toggleSetAsReference'
import checkLicence from 'app/xEcoApm/event/licence/check'
import updateLicence from 'app/xEcoApm/event/licence/update'
import reportExport from 'app/xEcoApm/event/report/export'
import previousTabOrBack from 'app/xEcoApm/event/previousTabOrBack'
import nextTab from 'app/xEcoApm/event/nextTab'
import toggleFilter from 'app/xEcoApm/event/toggleFilter'
import toggleActions from 'app/xEcoApm/event/toggleActions'

export default {
  acl: {
    acl,
    cell,
  },
  onOpen: {
    search: search(null),
    infinite: infinite(null),
    infiniteShort: infinite('short'),
    infiniteVerbose: infinite('verbose'),
    infiniteWithDrafts: infiniteWithDrafts(null),
    infiniteShortWithDrafts: infiniteWithDrafts('short'),
    searchDevices: searchDevices(null),
    getStatus: listStatus,
    getConnectionStatus,
    getBatteryStatus,
    getBluetoothPeripheralName,
    infiniteWithQuery: chain(
      searchWith(['query']),
      infinite(null),
    ),
    infiniteRecents: recents,
    link: curry((
      module: Gaia.AppModule.Spec,
      component: Gaia.Component.Spec,
      event: Gaia.PlatformEvent,
    ) => {
      const state = component[G.STATE]
      const url = _url(null, module, component, event)
      const key = state.value?.[0]?.refs?.person?.[0]?.key

      return key
        ? `${url}/${key}`
        : null
    }),
    noop: curry((m, c, e) => []),
  },
  onBack: {
    backTwice: go(-2),
    stopMeasurementAndBack: chain(
      stopMeasurement,
      back,
    ),
    previousTabOrBack,
  },
  onClick: {
    back,
    detail,
    redirect,
    redirectWithSensorKey: withAttributeKey('sensor'),
    forceRedirect,
    forceRedirectAndDismissKeyboard: chain(
      dismissKeyboard,
      forceRedirect,
    ),
    validateRedirect: chain(
      dismissKeyboard,
      actionByKey('validateRedirect'),
    ),
    openPdf,
    pairDeviceAndRedirect: chain(
      pairDevice,
      redirectWithModelKey('sensor'), // set by setPairingSensorRef hook
    ),
    resetScan,
    togglePeripheralConnection,
    updateLicence,
    submitLicence: curry((m, c, e) => submit(m, c, e, 'licenceSubmit')),
    submitMeasurement: curry((m, c, e) => submit(m, c, e, 'measurementSubmit')),
    startMeasurement,
    stopMeasurement,
    generateReport,
    reportExport,
    stopReport,
    copyQRCode,
    openQRCode,
    setProjectAndBack: chain(
      setProject,
      back,
    ),
    nextTab,
    toggleFilter,
    checkLicenceAndRedirect: chain(
      checkLicence,
      redirect,
    ),
  },
  onChange: {
    persistChange,
    persistOption,
    persistCheck,
    persistCheckAndAction,
    toggleSetAsReference,
    persistTabAndRemember: chain(
      persistTab,
      remember,
      update,
    ),
    persistTabRememberAndToggleActions: chain(
      persistTab,
      remember,
      platform({
        mobile: toggleActions,
      }),
      update,
    ),
    debouncedPersistChangeRememberAndAction: debounce(chain(
      persistChange,
      remember,
      action,
    ), 500),
    persistChangeRememberAndAction: chain(
      persistChange,
      remember,
      action,
    ),
    persistCheckRememberAndAction: chain(
      persistCheck,
      remember,
      action,
    )
  },
  onCreate: {
    createRedirect,
  },
  onClose: {
    back,
    stopReportAndBack: chain(
      stopReport,
      back,
    ),
  },
  onPage: {
    remember,
  },
  onPublication: {
    updatePeripheralStatus,
  },
  onScroll: {
    persistScrollAndRemember: chain(
      persistScroll,
      remember,
    ),
  },
  onSearch: {
    searchRecreateAndToggleResultsList: chain(
      toggleItemsLists,
      searchAndRecreateWithListKey('resultsList'),
    )
  },
  getSelection: {
    listTankValues,
    listFrequencyValues,
    listUsPowerValues,
    listLocationValues,
    listTemperatureUnits,
    listDateCreated,
  },
  getLocationValues: {
    listLocationValues,
  },
  getStatus: {
    listStatus,
  },
  onScan: {
    showScanFeedback: displayScanFeedback(true),
  },
  onScanStop: {
    hideScanFeedback: displayScanFeedback(false),
  },
  onDiscover: {
    addToList: curry(async (
      module: Gaia.AppModule.Spec,
      component: Gaia.Component.Spec,
      event: Gaia.PlatformEvent,
    ) => {
      const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
      const { scanResult } = find(actionComponent)

      const { [G.STATE]: { discoveredDevices = [] } = {} } = event?.detail || event || {}

      const sortedDiscoveredDevices = discoveredDevices.sort((a, b) => (a.value.rssi < b.value.rssi && 1) || (a.value.rssi > b.value.rssi && -1) || 0)

      setKey(sortedDiscoveredDevices, 'data', scanResult[G.STATE])
      setKey({}, 'update', scanResult[G.STATE])

      await module[G.ADAPTER][G.UI].update(module)
    }),
  },
  query: {
    deviceServiceHasContactData: curry((m, c, e) => 'metatype:organisation AND publishServiceContactData:(true)'),
    userOrgsAsInstalledAtOrInstalledAt: join(
      fromUserOrgs('installedAt', 'installedAt'),
      excludeNotConfirmedForRoles,
    ),
  },
  formatXLabel: {
    formatAcousticSpectrumXLabel,
  },
  formatYLabel: {
    formatAcousticSpectrumYLabel,
    formatCavitationIntensityYLabel,
    formatMainFrequencyYLabel,
  },
}
